/* Pieces of the Audio tab the Voices page reuses (console ADR 0028): voice previews, the
   clone form, the quota bar and a live-test result. Calls go through the props, so each
   page decides which endpoint (per Account or the User's own library) answers. */

import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsSuperAdmin } from '../../../../context/AuthContext';
import type { Voice, VoiceNoteQuota, VoiceNoteTestResult, VoiceRegister } from '../../../../api/client';
import { Btn, Card, Icon, Spin, TextInput, Warn } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';
import { CLONE_ACCEPT, MAX_CLONE_FILES, MAX_CLONE_MB, languageName, skipKey } from './model';
import { capStyle, chip, fieldCol, iconBtn } from './styles';
import type { PreviewState } from './usePreview';

export interface Run { id: number; at: Date; register: VoiceRegister; voice: string; result: VoiceNoteTestResult }

export function PreviewButton({ state, onClick }: { state: PreviewState; onClick: (e: React.MouseEvent) => void }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <button
      type="button"
      title={state === 'playing' ? t('au.v.stop') : t('au.v.preview')}
      onClick={onClick}
      style={{ height: 26, flex: 'none', display: 'flex', alignItems: 'center', gap: 5, padding: '0 9px', borderRadius: 99, border: `1px solid ${state === 'playing' ? C.tintLine45 : C.borderHi}`, background: state === 'playing' ? C.tint16 : 'transparent', color: state === 'playing' ? C.purplePale : C.sub, font: f(500, 11.5), cursor: 'pointer' }}
    >
      {state === 'loading' ? <Spin size={11} /> : <Icon name={state === 'playing' ? 'square' : 'play'} size={11} />}
      {state === 'playing' ? t('au.v.stop') : t('au.v.preview')}
    </button>
  );
}

export function CloneCard({ clone, onCancel, onCreated }: { clone: (label: string, files: File[]) => Promise<Voice>; onCancel: () => void; onCreated: (v: Voice) => void }) {
  const { t } = useTranslation('assistantCompact');
  const [name, setName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const pick = (list: FileList | null) => {
    setError(null);
    const picked = Array.from(list ?? []);
    const big = picked.find((x) => x.size > MAX_CLONE_MB * 1024 * 1024);
    if (big) { setError(t('au.c.tooBig', { name: big.name, mb: MAX_CLONE_MB })); return; }
    setFiles((cur) => [...cur, ...picked].slice(0, MAX_CLONE_FILES));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onCreated(await clone(name.trim(), files));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('au.c.failed'));
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = !!name.trim() && files.length > 0 && consent && !busy;
  return (
    <Card clip={false} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12, borderColor: C.tintLine }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="mic" size={14} color={C.purpleLt} />
        <span style={{ font: f(600, 13), color: C.text }}>{t('au.c.title')}</span>
      </div>
      <label style={fieldCol}>
        <span style={capStyle}>{t('au.c.name')}</span>
        <TextInput value={name} onChange={setName} placeholder={t('au.c.namePh')} width={320} />
      </label>
      <div style={fieldCol}>
        <span style={capStyle}>{t('au.c.samples')}</span>
        <span style={{ font: f(400, 11.5), color: C.muted }}>{t('au.c.samplesHint', { n: MAX_CLONE_FILES, mb: MAX_CLONE_MB })}</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {files.map((x, i) => (
            <span key={`${x.name}-${i}`} style={chip}>
              <Icon name="music" size={11} color={C.muted} />
              <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.name}</span>
              <button type="button" title={t('remove')} onClick={() => setFiles((l) => l.filter((_, j) => j !== i))} className="cpt-hover-text" style={{ ...iconBtn, width: 18, height: 18 }}><Icon name="x" size={11} /></button>
            </span>
          ))}
          {files.length < MAX_CLONE_FILES && (
            <Btn kind="dashed" icon="upload" onClick={() => input.current?.click()}>{files.length ? t('au.c.addMore') : t('au.c.choose')}</Btn>
          )}
          <input ref={input} type="file" accept={CLONE_ACCEPT} multiple hidden onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', font: f(400, 12, 1.45), color: C.body }}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2, accentColor: C.purple }} />
        <span>{t('au.c.consent')}</span>
      </label>
      {error && <Warn>{error}</Warn>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Btn kind="primary" icon="check" disabled={!canSubmit} busy={busy} onClick={submit}>{t('au.c.create')}</Btn>
        <Btn kind="muted" disabled={busy} onClick={onCancel}>{t('cancel')}</Btn>
        <span style={{ font: f(400, 11.5), color: C.muted }}>{t('au.c.after')}</span>
      </div>
    </Card>
  );
}

export function QuotaBar({ quota }: { quota: VoiceNoteQuota | null }) {
  const { t } = useTranslation('assistantCompact');
  if (!quota) return null;
  const pct = quota.allowed ? Math.min(100, (quota.used / quota.allowed) * 100) : 100;
  return (
    <Card style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ font: f(600, 13), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{t('au.q.used', { ...quota })}</span>
        <span style={{ font: f(400, 11.5), color: C.muted }}>{t('au.q.hint')}</span>
      </div>
      <div style={{ height: 5, borderRadius: 99, background: C.track, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: quota.remaining > 0 ? C.purple : C.purplePale, transition: 'width .3s' }} />
      </div>
    </Card>
  );
}

/** `madeLabel` replaces "Would be sent" where a run is only a test of a voice (the Voices
 *  page), not a dry run of what the AI would send. */
export function RunCard({ run, madeLabel }: { run: Run; madeLabel?: string }) {
  const { t, i18n } = useTranslation('assistantCompact');
  const isSuperAdmin = useIsSuperAdmin();
  const r = run.result;
  const sent = r.outcome === 'sent';
  const src = r.audio_b64 ? `data:${r.mime ?? 'audio/mpeg'};base64,${r.audio_b64}` : null;
  return (
    <Card clip={false} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, borderColor: sent ? C.tintLine : C.border }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, font: f(600, 12.5), color: sent ? C.purplePale : C.sub }}>
          <Icon name={sent ? 'check-circle' : 'slash'} size={13} />
          {sent ? madeLabel ?? t('au.t.wouldSend') : t('au.t.wouldSkip')}
        </span>
        {!sent && <span style={{ font: f(400, 12), color: C.sub }}>— {t(`au.skip.${skipKey(r.skip_reason)}`)}</span>}
        <span style={{ flex: 1 }} />
        <span style={{ font: f(400, 11.5), color: C.muted }}>
          {[run.voice, t(run.register === 'hot' ? 'au.t.hot' : 'au.t.normal'), r.language && languageName(r.language, i18n.language), r.duration_s != null && t('au.t.seconds', { n: r.duration_s }), run.at.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })].filter(Boolean).join(' · ')}
        </span>
      </div>
      {src && <audio controls src={src} style={{ width: '100%', height: 36 }} />}
      {r.spoken && (
        <Field label={t('au.t.says')}>
          <span style={{ font: f(400, 13, 1.5), color: C.text }}>“{r.spoken}”</span>
        </Field>
      )}
      {isSuperAdmin && r.script && (
        <Field label={t('au.t.tagged')}>
          <span style={{ font: f(400, 11.5, 1.5), color: C.sub, fontFamily: 'var(--font-mono)' }}>{r.script}</span>
        </Field>
      )}
    </Card>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={capStyle}>{label}</span>
      {children}
    </div>
  );
}
